import type { Bar } from '../../lib/api';
import { formatPrice } from '../../lib/chartTheme';

/**
 * 图表读数条 —— 光标驱动。
 *
 * 改前这行只显示**最新一根**的收盘与涨跌，光标在图上移动时数字纹丝不动
 * （它是最新价，本就该不动）。需求是「移动十字光标时能看到对应数值」，
 * 故改为：光标悬停某根柱 → 显示那根柱的 OHLC + 涨跌；指针移出 → 回落最新价。
 *
 * 数据来自 `bars` 按日期索引，**不依赖 lightweight-charts 的 seriesData** ——
 * 估值曲线用了独立 priceScaleId，crosshair.mode:Normal 在多轴下可能取不到
 * 主序列的价格标签，自己按日期查更可靠。
 *
 * 末柱未收盘时给「未收盘」角标（需求 §F2.10 要求盘中柱有标识，
 * 此处是它唯一的呈现位置 —— 图表上尚未做淡化）。
 */
export function ReadoutBar({
  bar,
  change,
  pct,
  isLive,
  children,
}: {
  /** 光标所在柱；为 null 表示当前无光标，展示回落态 */
  bar: Bar | null;
  /**
   * 涨跌额与涨跌幅，由调用方**按上一根收盘自算**。
   *
   * ⚠️ 不能直接用 `bar.change` / `bar.pctChange`：腾讯日 K 只有 6 个字段
   * [日期,开,收,高,低,量]，不含涨跌幅，解析时越界取 nil → 落库恒为 0
   * （全库 25189 行里 23190 行是 0，见 docs/后端重构-Go.md §B.10）。
   * 直接显示会得到「收 3813.79　+0.00 +0.00%」这种自相矛盾的读数。
   * 周/月/年线由 aggregate 用上一周期收盘算出，值是对的，但为口径一致同样自算。
   */
  change: number | null;
  pct: number | null;
  /** 该柱是否为最后一根（即"当前"） */
  isLive: boolean;
  /** MA / BOLL / 估值等附加读数，由调用方按需插入 */
  children?: React.ReactNode;
}) {
  const dirCls = (pct ?? change ?? 0) >= 0 ? 'var(--up)' : 'var(--down)';

  return (
    <div
      // shrink-0 而非 shrink：读数条是固定高度的一行，被压缩会换行并撑破
      // --readout-h，进而挤压图表（"固定高度与实测不一致"的老问题）。
      // 溢出用横向滚动承载 —— 窄屏下先看到日期与 OHLC，MA/PE 靠滑动。
      className="num flex shrink-0 items-center gap-3 overflow-x-auto overflow-y-hidden whitespace-nowrap px-3 text-[12px]"
      style={{ height: 'var(--readout-h)', color: 'var(--text-2)' }}
    >
      {bar ? (
        <>
          <span className="shrink-0" style={{ color: 'var(--text-1)' }}>{bar.date}</span>
          <span className="shrink-0">开 <span style={{ color: 'var(--text-1)' }}>{formatPrice(bar.open)}</span></span>
          <span className="shrink-0">高 <span style={{ color: 'var(--text-1)' }}>{formatPrice(bar.high)}</span></span>
          <span className="shrink-0">低 <span style={{ color: 'var(--text-1)' }}>{formatPrice(bar.low)}</span></span>
          <span className="shrink-0">收 <span style={{ color: 'var(--text-1)' }}>{formatPrice(bar.close)}</span></span>
          {(pct !== null || change !== null) && (
            <span className="shrink-0" style={{ color: dirCls }}>
              {change !== null && `${change >= 0 ? '+' : ''}${change.toFixed(2)} `}
              {pct !== null && `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`}
            </span>
          )}
          {/* 盘中未收盘：该柱仍会被后续数据覆盖，读数需如实标注 */}
          {!bar.isComplete && (
            <span
              className="shrink-0 rounded px-1 text-[10px]"
              style={{ background: 'var(--bg-elevated)', color: 'var(--warn)' }}
              title="该柱尚未收盘，数值会随行情变化"
            >未收盘</span>
          )}
        </>
      ) : (
        // 无光标：显示最新一根的概览（保持「一眼看到当前价」的原有用法）
        <>
          <span className="shrink-0">● 最新</span>
          {isLive && <span className="shrink-0" style={{ color: 'var(--text-3)' }}>（移动光标查看历史）</span>}
        </>
      )}
      {children}
    </div>
  );
}